/**
 * Publishing preflight — structured issues for Publish panel + package export.
 * Critical (`error`) issues block package export (fail closed).
 */

export type PreflightLevel = "error" | "warning" | "info";

export type PreflightIssue = {
  level: PreflightLevel;
  code: string;
  message: string;
  /** Suggested remediation shown in the UI. */
  fix?: string;
};

export type PreflightProfile = "print" | "ebook" | "package";

export type PreflightInput = {
  title?: string;
  author?: string;
  chapters?: Array<{
    id?: string;
    title?: string;
    content?: string;
    kind?: string;
    chapterNumber?: number | null;
  }>;
  /** book.metadata bag */
  metadata?: Record<string, unknown> | null;
  /** Estimated or paginated page count */
  pageCount?: number;
  /** Cover has imported artwork (data URL or path) */
  hasCoverArt?: boolean;
  profile?: PreflightProfile;
  platform?: "kdp" | "ingramspark" | "custom" | string;
};

export type PreflightReport = {
  issues: PreflightIssue[];
  /** True when no error-level issues. */
  ok: boolean;
  errorCount: number;
  warningCount: number;
  generatedAt: string;
};

function publishingBag(metadata: Record<string, unknown> | null | undefined): Record<string, unknown> {
  if (!metadata) return {};
  const raw = metadata.publishing ?? metadata.publishingMetadata;
  return raw && typeof raw === "object" && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {};
}

import { chapterContentToPlainText } from "./chapterContent";

function wordCount(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

function chapterPlain(content: unknown): string {
  return chapterContentToPlainText(content);
}

export function runPreflight(input: PreflightInput): PreflightReport {
  const issues: PreflightIssue[] = [];
  const chapters = Array.isArray(input.chapters) ? input.chapters : [];
  const profile: PreflightProfile = input.profile || "print";
  const pub = publishingBag(input.metadata);
  const totalWords = chapters.reduce((sum, ch) => sum + wordCount(chapterPlain(ch.content)), 0);
  const nonEmpty = chapters.filter((ch) => wordCount(chapterPlain(ch.content)) > 0);
  const spineChapters = chapters.filter((ch) => (ch.kind || "chapter") === "chapter");
  const hasCopyright = chapters.some((ch) => ch.kind === "copyright");
  const hasTitlePage = chapters.some((ch) => ch.kind === "title_page" || ch.kind === "half_title");

  if (!String(input.title || "").trim()) {
    issues.push({
      level: "error",
      code: "title.missing",
      message: "Book title is missing.",
      fix: "Set a title in book settings before exporting.",
    });
  }

  if (!String(input.author || "").trim()) {
    issues.push({
      level: "warning",
      code: "author.missing",
      message: "Author name is empty.",
      fix: "Add an author name for metadata and title pages.",
    });
  }

  if (nonEmpty.length === 0) {
    issues.push({
      level: "error",
      code: "content.empty",
      message: "Manuscript has no chapter content.",
      fix: "Add text to at least one spine item before exporting.",
    });
  }

  if (spineChapters.length === 0 && chapters.length > 0) {
    issues.push({
      level: "warning",
      code: "spine.no_chapters",
      message: "Spine has no chapter items (only front/back matter).",
      fix: "Add at least one chapter, or confirm matter-only export is intentional.",
    });
  }

  const emptyTitled = spineChapters.filter(
    (ch) => (ch.title || "").trim() && wordCount(chapterPlain(ch.content)) === 0
  );
  if (emptyTitled.length > 0) {
    issues.push({
      level: "warning",
      code: "content.empty_chapters",
      message: `${emptyTitled.length} chapter(s) have titles but no body text.`,
      fix: "Fill empty chapters or remove them from the spine.",
    });
  }

  if (profile === "print" || profile === "package") {
    if (!hasTitlePage) {
      issues.push({
        level: "warning",
        code: "matter.title_page",
        message: "No title page in the spine.",
        fix: "Insert a Title page or Half title from the spine menu.",
      });
    }
    if (!hasCopyright) {
      issues.push({
        level: "warning",
        code: "matter.copyright",
        message: "No copyright page in the spine.",
        fix: "Insert a Copyright page before exporting print files.",
      });
    }
    const minWords = input.platform === "ingramspark" ? 1000 : 500;
    if (totalWords > 0 && totalWords < minWords) {
      issues.push({
        level: "warning",
        code: "content.short",
        message: `Manuscript is short (${totalWords} words; typical minimum ~${minWords}).`,
        fix: "Confirm length meets your distributor’s guidelines.",
      });
    }
  }

  const description = typeof pub.description === "string" ? pub.description.trim() : "";
  const isbnEbook = typeof pub.isbnEbook === "string" ? pub.isbnEbook.trim() : "";
  const isbnPrint = typeof pub.isbnPrint === "string" ? pub.isbnPrint.trim() : "";
  const language = typeof pub.language === "string" ? pub.language.trim() : "";
  const bisac = Array.isArray(pub.bisacCodes) ? pub.bisacCodes : [];

  if (!description) {
    issues.push({
      level: "warning",
      code: "meta.description",
      message: "Publishing description is missing.",
      fix: "Add a description in the Publishing tab (web) or book metadata.",
    });
  }

  if ((profile === "ebook" || profile === "package") && !isbnEbook && !isbnPrint) {
    issues.push({
      level: "warning",
      code: "meta.isbn",
      message: "No ISBN set for ebook/print.",
      fix: "Add an ISBN in publishing metadata (optional for draft KDP ebooks).",
    });
  }

  if (!language) {
    issues.push({
      level: "info",
      code: "meta.language",
      message: "Language not set in publishing metadata (defaults to English).",
      fix: "Set language in publishing metadata for store listings.",
    });
  }

  if (bisac.length === 0 && (profile === "ebook" || profile === "package")) {
    issues.push({
      level: "warning",
      code: "meta.bisac",
      message: "No BISAC subject codes.",
      fix: "Pick at least one BISAC code for store categorization.",
    });
  }

  if (profile === "print" || profile === "package") {
    if (!input.hasCoverArt) {
      issues.push({
        level: profile === "package" ? "error" : "warning",
        code: "cover.missing",
        message: "Cover artwork is not set.",
        fix: "Import a cover image in the Cover editor before packaging.",
      });
    }
    const pages = input.pageCount ?? 0;
    if (pages > 0 && pages < 24) {
      issues.push({
        level: "warning",
        code: "print.page_count_low",
        message: `Estimated page count is ${pages} (many printers require ≥24).`,
        fix: "Increase content or adjust trim/typography.",
      });
    }
    if (pages > 0) {
      issues.push({
        level: "info",
        code: "print.page_count",
        message: `Estimated interior page count: ${pages}.`,
      });
    }
  }

  const errorCount = issues.filter((i) => i.level === "error").length;
  const warningCount = issues.filter((i) => i.level === "warning").length;
  return {
    issues,
    ok: errorCount === 0,
    errorCount,
    warningCount,
    generatedAt: new Date().toISOString(),
  };
}

export function hasBlockingPreflightErrors(report: PreflightReport): boolean {
  return !report.ok;
}
